import React from 'react';
import ReactModal from 'react-modal';
import { cn } from '@express-labs/raven-tools';
import { Brander } from '@express-labs/raven-brander';
import FocusCloseModal from './subcomponents/FocusCloseModal/FocusCloseModal';
import s from './Modal.scss';

type ModalView = 'default' | 'full';

export interface ModalProps {
  className?: string;
  closeIconClassName?: string;
  closeTimeoutMS?: number;
  contentLabel: string;
  hideCloseButton?: boolean;
  isCloseable?: boolean;
  isOpen: boolean;
  onModalClose?: () => void;
  overlayClassName?: string;
  focusOnOpen?: boolean;
  focusOnUpdate?: boolean;
  view?: ModalView;
  shouldCloseOnOverlayClick?: boolean;
  setAppElement?: string | HTMLElement;
  children: React.ReactNode;
}

interface ModalState {
  propIsOpen: boolean;
}

/**
 * Our Modal component wraps and extends the  [React Modal](https://github.com/reactjs/react-modal) package.

All props are passed through to ReactModal with the exception of `onRequestClose` -- use onModalClose instead.

Any props passed through take precedence over props set internally by this component with the exception of onRequestClose.

IMPORTANT: In order to be fully WCAG compliant you MUST return focus to the button or link that spawned this modal if the user clicks on the close button!

## Examples
```html
<Modal contentLabel="Simple Example">
  <h1>Hello World!</h1>
  <p>Default modal</p>
</Modal>

<!-- actual example from the app -->
<Modal
  isOpen={this.state.isOpen}
  role="dialog"
  aria-labelledby="login-dialog-title"
  aria-describedby="login-dialog-desc"
  className={s.loginContainerModal}
  onModalClose={this.onModalWindowClose}
>
  <div>
    <h1 id="login-dialog-title" className="srOnly">
      Sign in or checkout as guest modal dialog
    </h1>
    <p id="login-dialog-desc" className="srOnly">
      This dialog contains a form for signing into Express and a button for checking out
      as a guest.
    </p>
    <LoginContainer
      onLogin={this.onLogin}
      showGuest
    />
  </div>
</Modal>
<
```
This is a map of the css classes for this component.  You can hook into these classes to further customize the Modal.

## Global Class Names
```html
<div class="ReactModalPortal">
  <div class="ReactModal__Overlay ReactModal__Overlay--after-open ...">
    <div class="ReactModal__Content ReactModal__Content--after-open [props.className] ,,,">
      <section>
        ... [your content] ...
      </section>
      <section>
        <button>[ close button ]</button>
      </section>
    </div>
  </div>
</div>
```
**/

// We need to override ReactModal displayName for our Enzyme tests

(ReactModal as any).displayName = 'Modal';
class Modal extends React.Component<ModalProps, ModalState> {
  constructor(props: ModalProps) {
    super(props);
    this.closeModal = this.closeModal.bind(this);
    const { isOpen, setAppElement = document.body } = props;
    if (isOpen === true) ReactModal.setAppElement(setAppElement);
    this.state = {
      propIsOpen: isOpen,
    };
  }

  public static displayName = 'Modal';

  closeModal() {
    const { onModalClose } = this.props;
    if (typeof onModalClose === 'function') onModalClose();
  }

  static getDerivedStateFromProps(
    nextProps: ModalProps,
    prevState: Record<string, unknown>
  ) {
    if (prevState.propIsOpen !== true && nextProps.isOpen === true) {
      ReactModal.setAppElement(nextProps.setAppElement || document.body);
      return {
        propIsOpen: nextProps.isOpen,
      };
    }
    return null;
  }

  render() {
    const {
      className,
      closeIconClassName,
      isCloseable = true,
      hideCloseButton = false,
      isOpen,
      onModalClose,
      overlayClassName,
      focusOnOpen,
      focusOnUpdate,
      view,
      shouldCloseOnOverlayClick,
      ...passThroughProps
    } = this.props;

    return (
      <ReactModal
        shouldCloseOnOverlayClick
        isOpen={this.props.isOpen}
        overlayClassName={cn(overlayClassName, s.reactModalOverlay)}
        className={cn(className, s.reactModal)}
        onRequestClose={this.closeModal}
        {...passThroughProps}
      >
        <Brander>
          <section className={s.modalContent}>{this.props.children}</section>

          {isCloseable && !hideCloseButton && (
            <section
              className={cn(
                s.closeButton,
                s[view as keyof typeof view],
                closeIconClassName
              )}
            >
              <FocusCloseModal
                onButtonClick={this.closeModal}
                focusOnOpen={focusOnOpen}
                focusOnUpdate={focusOnUpdate}
                isOpen={isOpen}
              />
            </section>
          )}
        </Brander>
      </ReactModal>
    );
  }
}

export default Modal;
